const LOCALE = 'en-US';

const formatDate = (date) =>
  date.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
  });

const formatMonth = (date) =>
  date.toLocaleDateString(LOCALE, {
    month: 'long',
  });

export const formatTime = (date) =>
  date.toLocaleTimeString(LOCALE, {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
  });

// Adds a specified number of months to a date.
export const addMonths = (date, months) => {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + months);
  return newDate;
};

// Adds a specified number of weeks to a date.
export const addWeeks = (date, weeks) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + weeks * 7);
  return newDate;
};

// Returns a readable date and time range (e.g. "November 15 at 10:00 AM - 11:00 AM").
export const formatDateTimeRange = ({ startAt, endAt }) => {
  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  const isSameDate =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  const startDateStr = formatDate(startDate);
  const endDateStr = formatDate(endDate);
  const startTimeStr = formatTime(startDate);
  const endTimeStr = formatTime(endDate);

  return isSameDate
    ? `${startDateStr} at ${startTimeStr} - ${endTimeStr}`
    : `${startDateStr} at ${startTimeStr} - ${endDateStr} at ${endTimeStr}`;
};

// Returns a readable month and year (e.g. "November 2025").
export const formatMonthYear = (date) => `${formatMonth(date)} ${date.getFullYear()}`;

// Returns a readable time range (e.g. "10:00 AM - 11:00 AM").
export const formatTimeRange = ({ startAt, endAt }) =>
  `${formatTime(new Date(startAt))} – ${formatTime(new Date(endAt))}`;

// Returns a readable week range (e.g. "November 15 - 21, 2024").
export const formatWeekRange = (date) => {
  const weekDates = getWeekDates(date);
  const start = weekDates[0];
  const end = weekDates[6];

  const isSameMonth = start.getMonth() === end.getMonth();

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  const isSameYear = startYear === endYear;

  if (isSameMonth && isSameYear) {
    return `${formatMonth(start)} ${start.getDate()} - ${end.getDate()}, ${startYear}`;
  }

  if (isSameYear) {
    return `${formatDate(start)} - ${formatDate(end)}, ${startYear}`;
  }

  return `${formatDate(start)}, ${startYear} - ${formatDate(end)}, ${endYear}`;
};

// Given a time string (e.g. "3:00 PM"), returns the hour (15).
export const getHour = (timeStr) => {
  const [time, period] = timeStr.split(' ');
  const hour12 = Number(time.split(':')[0]);
  return (hour12 % 12) + (period === 'PM' ? 12 : 0);
};

// Returns the dates of the month containing the given date. In order to fill the calendar
// grid, also includes dates from the previous / next month.
export const getMonthDates = (date) => {
  const firstDateOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const firstDateIdx = firstDateOfMonth.getDay();

  const firstDateOfGrid = new Date(firstDateOfMonth);
  firstDateOfGrid.setDate(firstDateOfMonth.getDate() - firstDateIdx);

  const lastDateOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const daysInMonth = lastDateOfMonth.getDate();
  const totalDays = Math.ceil((firstDateIdx + daysInMonth) / 7) * 7;

  return Array.from({ length: totalDays }, (_, i) => {
    const gridDate = new Date(firstDateOfGrid);
    gridDate.setDate(firstDateOfGrid.getDate() + i);
    return gridDate;
  });
};

// Returns the 7 dates in the week containing the given date.
export const getWeekDates = (date) => {
  const firstDateOfWeek = new Date(date);
  firstDateOfWeek.setDate(firstDateOfWeek.getDate() - firstDateOfWeek.getDay());

  return Array.from({ length: 7 }, (_, i) => {
    const weekDate = new Date(firstDateOfWeek);
    weekDate.setDate(firstDateOfWeek.getDate() + i);
    return weekDate;
  });
};

// Returns true if the given date is in the same month as the reference date.
export const isInSameMonth = (date, refDate) => date.getMonth() === refDate.getMonth();

// Returns true if the given date is today.
export const isToday = (date) => date.toDateString() === new Date().toDateString();

// Returns true if the given time string falls within standard workday hours (8 AM - 5 PM).
export const isWithinWorkday = (timeStr) => {
  const hour = getHour(timeStr);
  return hour >= 8 && hour < 17;
};
